import styled, { css } from 'styled-components';
import { Link } from 'gatsby';
import { OutlineStyles } from '../../styles';
import { SideNavGroupToggleProps } from '../../SideNav';
import { sidenavTokens } from '../../tokens';
import { resolveNestingPadding } from '../../helpers';

type StyledSideNavGroupToggleProps = Pick<
  SideNavGroupToggleProps,
  'alwaysShowMenuItems' | 'isActive' | 'nestingLevel' | 'hasActiveChild'
>;

const SideNavGroupToggleStyles = css<StyledSideNavGroupToggleProps>`
  align-items: center;
  background-color: transparent;
  border: none;
  color: ${sidenavTokens.FontColorBaseDefault};
  display: flex;
  font-weight: ${sidenavTokens.GroupItemFontWeightBaseDefault};
  line-height: ${sidenavTokens.GroupToggleFontLeadingBaseDefault};
  height: auto;
  justify-content: space-between;
  margin: 0;
  padding-top: ${sidenavTokens.ItemPaddingBaseOpenVertical};
  padding-bottom: ${sidenavTokens.ItemPaddingBaseOpenVertical};
  padding-left: ${({ nestingLevel }) => resolveNestingPadding({ nestingLevel, type: 'item' })};
  padding-right: ${sidenavTokens.ItemSpacingPaddingHorizontalBaseDefault};
  transition: background-color ${sidenavTokens.TransitionDurationBaseDefault}
    ${sidenavTokens.TransitionCurveBaseDefault};
  width: 100%;
  text-decoration: none;

  &:hover {
    cursor: pointer;
    text-decoration: none;
  }

  ${OutlineStyles}

  ${({ isActive, nestingLevel }) =>
    isActive &&
    css`
      background-color: ${sidenavTokens.ItemBackgroundColorActiveDefault};
      box-shadow: ${sidenavTokens.ItemBorderWidthBaseDown} 0 0 0 ${sidenavTokens.ItemBorderColorBaseDown} inset;
      color: ${sidenavTokens.FontColorBaseDefault};
      font-weight: ${sidenavTokens.GroupItemFontWeightBaseActive};
      padding-left: ${resolveNestingPadding({ nestingLevel, type: 'item' })};
    `}

  ${({ hasActiveChild }) =>
    hasActiveChild &&
    css`
      color: ${sidenavTokens.FontColorBaseDefault};
      font-weight: ${sidenavTokens.GroupItemFontWeightBaseActive};
    `}
`;

export const StyledSideNavGroupToggle = styled.button<StyledSideNavGroupToggleProps>`
  ${SideNavGroupToggleStyles}
`;

export const StyledSideNavGroupToggleLink = styled(Link).withConfig<StyledSideNavGroupToggleProps>({
  shouldForwardProp: (prop, dv) =>
    !['alwaysShowMenuItems', 'isActive', 'nestingLevel', 'hasActiveChild'].includes(prop) || dv(prop),
})`
  ${SideNavGroupToggleStyles}
`;

export const StyledSideNavGroupToggleAnchor = styled('a').withConfig<StyledSideNavGroupToggleProps>({
  shouldForwardProp: (prop, dv) =>
    !['alwaysShowMenuItems', 'isActive', 'nestingLevel', 'hasActiveChild'].includes(prop) || dv(prop),
})`
  ${SideNavGroupToggleStyles}
`;

export const StyledSideNavGroupToggleLabel = styled.span`
  flex-grow: 1;
  flex-shrink: 0;
  line-height: 20px;
`;
